import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah8k53p14a1h3mr6fa0";

type Status = "idle" | "submitting" | "success" | "error";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
};

const inputClass =
  "w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30 transition-colors";

const labelClass = "block text-sm font-medium text-foreground-800 mb-1.5";

export default function ReportForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [reference, setReference] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") {
      return;
    }

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const detail = String(formData.get("detail") ?? "");
    if (detail.length > 500) {
      setFormError("争议经过描述不能超过 500 个字符，请精简后再提交。");
      setStatus("error");
      return;
    }

    const guard = String(formData.get("contact_alt") ?? "").trim();
    if (guard) {
      formEl.reset();
      setReference("");
      setFormError("");
      setStatus("success");
      return;
    }
    formData.delete("contact_alt");
    formData.delete("agree");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        params.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = responseText ? (JSON.parse(responseText) as FormResponse) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);
      const code = parsed?.code;

      if (response.ok && code === "OK" && !isSpam) {
        const suffix = Date.now().toString().slice(-8);
        setReference(`BL-${suffix}`);
        setFormError("");
        setStatus("success");
        formEl.reset();
      } else {
        setFormError(serverMsg || "提交失败，请检查内容后重试。");
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，提交未成功，请稍后重试。");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="bg-background-50 border border-accent-200 rounded-lg p-6 md:p-8 text-center">
        <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-800">
          <i className="ri-checkbox-circle-line text-3xl" aria-hidden="true"></i>
        </span>
        <h3 className="mt-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">线索已提交</h3>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600 max-w-lg mx-auto">
          平台将在 24 小时内完成初步受理，核查进度与处理结果会通过你填写的联系邮箱同步。请留意来自爆料直播的邮件。
        </p>
        {reference && (
          <p className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800">
            受理编号
            <strong className="font-heading text-base text-primary-600">{reference}</strong>
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            setStatus("idle");
            setReference("");
          }}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium bg-foreground-950 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" aria-hidden="true"></i>
          再提交一条线索
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="baoliao-report-form"
      onSubmit={handleSubmit}
      noValidate
      className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6"
    >
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className="ri-live-line" aria-hidden="true"></i>
        </span>
        <h2 className="font-heading text-lg font-bold text-foreground-950">提交举报线索</h2>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
        <div>
          <label className={labelClass} htmlFor="reportType">
            举报类型 <span className="text-primary-600">*</span>
          </label>
          <select id="reportType" name="reportType" required defaultValue="" className={inputClass}>
            <option value="" disabled>
              请选择举报类型
            </option>
            <option value="画面中断反馈">画面中断反馈</option>
            <option value="主播违规投诉">主播违规投诉</option>
            <option value="消费与打赏争议">消费与打赏争议</option>
            <option value="版权与素材争议">版权与素材争议</option>
            <option value="其他争议">其他争议</option>
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="platform">
            直播平台 <span className="text-primary-600">*</span>
          </label>
          <input
            id="platform"
            name="platform"
            type="text"
            required
            placeholder="例如：星野直播"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="room">
            直播间名称 <span className="text-primary-600">*</span>
          </label>
          <input
            id="room"
            name="room"
            type="text"
            required
            placeholder="例如：深夜游戏夜话"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="occurredAt">
            发生时间 <span className="text-primary-600">*</span>
          </label>
          <input
            id="occurredAt"
            name="occurredAt"
            type="datetime-local"
            required
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="streamer">
            涉及主播或账号
          </label>
          <input
            id="streamer"
            name="streamer"
            type="text"
            placeholder="选填，便于快速定位"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="email">
            联系邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="用于接收受理编号与处理结果"
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="evidence">
            证据材料链接
          </label>
          <input
            id="evidence"
            name="evidence"
            type="text"
            placeholder="选填，可填写录屏、截图或对话记录的分享链接"
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="detail">
            争议经过描述 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="detail"
            name="detail"
            required
            rows={5}
            maxLength={500}
            placeholder="请描述直播争议发生的时间、经过与你的诉求，不超过 500 个字符。"
            className={`${inputClass} resize-none`}
          ></textarea>
          <p className="mt-1.5 text-xs text-foreground-500">建议包含中断时长、违规话术时间段等可核验细节。</p>
        </div>
      </div>

      <div className="form-field-guard" aria-hidden="true">
        <label htmlFor="contact_alt">备用联系号码</label>
        <input
          id="contact_alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <label className="mt-5 flex items-start gap-2.5 text-sm text-foreground-700 cursor-pointer">
        <input
          type="checkbox"
          name="agree"
          required
          className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400/40"
        />
        <span>
          我已阅读并同意举报须知，承诺所提交内容真实、可核验，并理解恶意举报将被限制提交权限。
        </span>
      </label>

      {status === "error" && formError && (
        <p className="mt-4 flex items-start gap-2 px-3.5 py-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
          <span>{formError}</span>
        </p>
      )}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden="true"></i>
          {status === "submitting" ? "提交中…" : "提交举报线索"}
        </button>
        <p className="text-xs text-foreground-500">提交后请留意邮箱，受理编号与进度通知都会发送到该邮箱。</p>
      </div>
    </form>
  );
}